import { useCallback, useState } from 'react';
import axios from 'axios';
import { useSWRConfig } from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { postDataFetcher } from '@/utils/apis';

export type ApprovalDecisionAction = 'confirm' | 'reject' | 'hold';

export type UseApprovalDecisionOptions = {
  onSuccess?: (action: ApprovalDecisionAction) => void;
  onError?: (error: unknown, action: ApprovalDecisionAction) => void;
};

/** 품의 결재 액션 mutation — 결재(승인) / 반려 / 보류 */
const useApprovalDecision = (options?: UseApprovalDecisionOptions) => {
  const { mutate } = useSWRConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const decide = useCallback(
    async (
      action: ApprovalDecisionAction,
      documentId: string,
      opinion?: string,
    ): Promise<void> => {
      setIsSubmitting(true);
      const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
      const trimmed = opinion?.trim();
      try {
        await postDataFetcher<Record<string, unknown>, Record<string, unknown>>(
          `${baseKey}/${action}`,
          trimmed ? { opinion: trimmed } : {},
        );
        // 단건 캐시 무효화 + 재검증 — 문자열 key(useApproval) + 튜플 key(useApprovalView) 동시 매칭.
        await mutate(
          (cacheKey) =>
            cacheKey === baseKey ||
            (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
        );
        options?.onSuccess?.(action);
      } catch (error) {
        options?.onError?.(error, action);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutate, options],
  );

  return { decide, isSubmitting };
};

/**
 * 결재 액션 실패 시 status code → 사용자 메시지(번역 key) 매핑.
 * 호출부에서 `t(getApprovalDecisionErrorMessage(error, action))` 로 사용.
 */
export const getApprovalDecisionErrorMessage = (
  error: unknown,
  action: ApprovalDecisionAction,
): string => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const fallback =
    action === 'reject'
      ? '반려에 실패했습니다.'
      : action === 'hold'
      ? '보류에 실패했습니다.'
      : '결재에 실패했습니다.';
  switch (status) {
    case 403:
      return '현재 결재 차례가 아닙니다.';
    case 404:
      return '문서를 찾을 수 없습니다.';
    case 422:
      return '결재할 수 없는 상태의 품의입니다.';
    default:
      return fallback;
  }
};

export default useApprovalDecision;
